<section class="secure-flow" id="secure-flow" aria-labelledby="secure-flow-title">
  <div class="secure-flow__inner">
    <header class="secure-flow__header">
      <div class="secure-flow__heading">
        <p class="secure-flow__eyebrow">SECURE BY DESIGN</p>
        <h2 id="secure-flow-title" class="secure-flow__title">
          Your model. Our benchmarking data. Fully protected.
        </h2>
      </div>
      <p class="secure-flow__lede">
        The platform uses confidential computing to ensure submitted AI models
        and benchmarking datasets remain isolated during evaluation.
      </p>
    </header>

    <div class="secure-flow__card">
      <div class="secure-flow__stage">
        <div class="secure-flow__diagram-col">
          <p class="secure-flow__frame-label">MODEL_LIFECYCLE · SECURE FLOW</p>

          <div class="secure-flow__diagram">
            <svg
              class="secure-flow__svg"
              viewBox="0 0 760 700"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              [attr.aria-label]="'Secure evaluation flow, step ' + current.id + ' of 10: ' + current.title"
            >
              <defs>
                <filter id="sf-glow" x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur stdDeviation="3.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <marker id="sf-arr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
                  <path d="M1 1l5 2.5-5 2.5z" fill="var(--sf-primary)" opacity=".8" />
                </marker>
              </defs>

              <rect
                class="trust-zone"
                [class.glow]="current.trust"
                x="20"
                y="132"
                width="720"
                height="432"
                rx="20"
              />
              <text class="trust-lbl" [class.glow]="current.trust" x="48" y="161">
                Trust boundary · Sealed zone
              </text>

              <text class="tier-lbl" x="38" y="30">Tier 1 · Setup</text>
              <text class="tier-lbl" x="66" y="188">Tier 2 · TEE</text>
              <text class="tier-lbl" x="66" y="424">Tier 3 · Evaluation</text>
              <text class="tier-lbl" x="48" y="578">Tier 4 · Results</text>

              <g class="fo-node tee-shell" [class.is-active]="isNodeActive('n-tee-shell')"></g>

              <path id="p-user-enc" class="fp" [class.lit]="isPathActive('p-user-enc')" d="M158,79 H302" />
              <path id="p-tee-enc-attest" class="fp" [class.lit]="isPathActive('p-tee-enc-attest')" marker-end="url(#sf-arr)" d="M468,190 C548,166 520,124 420,118" />
              <path id="p-tee-owner-attest" class="fp" [class.lit]="isPathActive('p-tee-owner-attest')" marker-end="url(#sf-arr)" d="M468,176 C530,150 540,100 568,84" />
              <path id="p-owner-tee-data" class="fp" [class.lit]="isPathActive('p-owner-tee-data')" marker-end="url(#sf-arr)" d="M594,118 C570,146 536,178 468,204" />
              <text
                class="att-lbl"
                [class.svg-hidden]="!current.attX"
                [class.lit]="!!current.attX"
                [attr.x]="current.attX || 0"
                [attr.y]="current.attY || 0"
              >
                attestation report
              </text>
              <path id="p-enc-buf" class="fp" [class.lit]="isPathActive('p-enc-buf')" d="M340,118 C260,146 264,176 292,190" />
              <path id="p-buf-q" class="fp" [class.lit]="isPathActive('p-buf-q')" d="M380,236 C330,252 266,276 198,300" />
              <path id="p-buf-dec" class="fp" [class.lit]="isPathActive('p-buf-dec')" d="M380,236 C430,252 494,276 562,300" />
              <path id="p-proc-pol" class="fp" [class.lit]="isPathActive('p-proc-pol')" d="M380,236 C344,330 232,392 133,440" />
              <path id="p-proc-scr" class="fp" [class.lit]="isPathActive('p-proc-scr')" d="M380,236 C366,330 326,392 297,440" />
              <path id="p-proc-eval" class="fp" [class.lit]="isPathActive('p-proc-eval')" d="M380,236 C398,330 434,392 461,440" />
              <path id="p-eval-clean" class="fp" [class.lit]="isPathActive('p-eval-clean')" d="M520,477 C536,477 550,477 566,477" />
              <path id="p-eval-lb" class="fp" [class.lit]="isPathActive('p-eval-lb')" d="M461,514 C462,552 420,568 380,590" />

              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-user-enc')">
                <animateMotion dur="1s" repeatCount="indefinite" rotate="auto"><mpath href="#p-user-enc" /></animateMotion>
              </circle>
              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-tee-enc-attest')">
                <animateMotion dur="1.25s" repeatCount="indefinite" rotate="auto"><mpath href="#p-tee-enc-attest" /></animateMotion>
              </circle>
              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-tee-owner-attest')">
                <animateMotion dur="1.15s" repeatCount="indefinite" rotate="auto"><mpath href="#p-tee-owner-attest" /></animateMotion>
              </circle>
              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-owner-tee-data')">
                <animateMotion dur="1.15s" repeatCount="indefinite" rotate="auto"><mpath href="#p-owner-tee-data" /></animateMotion>
              </circle>
              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-enc-buf')">
                <animateMotion dur="1.2s" repeatCount="indefinite" rotate="auto"><mpath href="#p-enc-buf" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-buf-q')">
                <animateMotion dur=".9s" repeatCount="indefinite" rotate="auto"><mpath href="#p-buf-q" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-buf-dec')">
                <animateMotion dur=".9s" repeatCount="indefinite" rotate="auto"><mpath href="#p-buf-dec" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-proc-pol')">
                <animateMotion dur=".95s" repeatCount="indefinite" rotate="auto"><mpath href="#p-proc-pol" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-proc-scr')">
                <animateMotion dur=".95s" repeatCount="indefinite" rotate="auto"><mpath href="#p-proc-scr" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-proc-eval')">
                <animateMotion dur="1s" repeatCount="indefinite" rotate="auto"><mpath href="#p-proc-eval" /></animateMotion>
              </circle>
              <circle class="ball" r="4.5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-eval-clean')">
                <animateMotion dur=".95s" repeatCount="indefinite" rotate="auto"><mpath href="#p-eval-clean" /></animateMotion>
              </circle>
              <circle class="ball" r="5" fill="var(--sf-primary)" filter="url(#sf-glow)" [class.show]="isBallActive('b-eval-lb')">
                <animateMotion dur="1.1s" repeatCount="indefinite" rotate="auto"><mpath href="#p-eval-lb" /></animateMotion>
              </circle>

              <g class="fo-node" [class.is-active]="isNodeActive('n-user')">
                <foreignObject x="42" y="40" width="116" height="78">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <img src="/assets/images/secure-flow/icon-user.png" alt="" width="20" height="20" />
                    </div>
                    <div class="nt">You</div>
                    <div class="ns">Sign in &amp; register</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-enc')">
                <foreignObject x="302" y="40" width="156" height="78">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <img src="/assets/images/secure-flow/icon-lock.png" alt="" width="20" height="20" />
                    </div>
                    <div class="nt">Browser</div>
                    <div class="ns">Locks model</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-data-owner')">
                <foreignObject x="568" y="40" width="146" height="78">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <ellipse cx="12" cy="5" rx="9" ry="3" />
                        <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                        <path d="M3 12A9 3 0 0 0 21 12" />
                      </svg>
                    </div>
                    <div class="nt">Data owner</div>
                    <div class="ns">Test data</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-buf')">
                <foreignObject x="292" y="144" width="176" height="90">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                    </div>
                    <div class="nt">TEE</div>
                    <div class="ns">Sealed vault</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-queue')">
                <foreignObject x="142" y="300" width="112" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 13v8" />
                        <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                        <path d="m8 17 4-4 4 4" />
                      </svg>
                    </div>
                    <div class="nt">Queue</div>
                    <div class="ns">Waits its turn</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-dec')">
                <foreignObject x="506" y="300" width="112" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <img src="/assets/images/secure-flow/icon-lock.png" alt="" width="20" height="20" />
                    </div>
                    <div class="nt">Decrypt</div>
                    <div class="ns">Inside only</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-pol')">
                <foreignObject x="74" y="440" width="118" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                    </div>
                    <div class="nt">Policy</div>
                    <div class="ns">Nothing leaks</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-scr')">
                <foreignObject x="238" y="440" width="118" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                        <path d="m10 13-2 2 2 2" />
                        <path d="m14 13 2 2-2 2" />
                      </svg>
                    </div>
                    <div class="nt">Eval scripts</div>
                    <div class="ns">Test data in</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-eval')">
                <foreignObject x="402" y="440" width="118" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <img src="/assets/images/secure-flow/icon-leaderboard.png" alt="" width="20" height="20" />
                    </div>
                    <div class="nt">Model scored</div>
                    <div class="ns">Out of sight</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-clean')">
                <foreignObject x="566" y="440" width="118" height="74">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        <line x1="10" x2="10" y1="11" y2="17" />
                        <line x1="14" x2="14" y1="11" y2="17" />
                      </svg>
                    </div>
                    <div class="nt">Secure cleanup</div>
                    <div class="ns">Model/data wiped</div>
                  </div>
                </foreignObject>
              </g>

              <g class="fo-node" [class.is-active]="isNodeActive('n-lb')">
                <foreignObject x="292" y="590" width="176" height="58">
                  <div xmlns="http://www.w3.org/1999/xhtml" class="nc">
                    <div class="ico">
                      <img src="/assets/images/secure-flow/icon-leaderboard.png" alt="" width="20" height="20" />
                    </div>
                    <div class="nt">Leaderboard</div>
                    <div class="ns">See your rank</div>
                  </div>
                </foreignObject>
              </g>
            </svg>
          </div>
        </div>

        <aside class="secure-flow__panel" aria-live="polite">
          <div class="secure-flow__panel-main">
            <div class="secure-flow__panel-meta">
              <p class="secure-flow__step-label">Step {{ stepNumber }}</p>
              <p class="secure-flow__step-count">{{ current.id }} of {{ steps.length }}</p>
            </div>

            <div class="secure-flow__panel-copy">
              <h3 class="secure-flow__step-title">{{ current.title }}</h3>
              <p class="secure-flow__step-body">{{ current.body }}</p>
              <span *ngIf="current.chip" class="secure-flow__chip">{{ current.chip }}</span>
            </div>
          </div>

          <div class="secure-flow__panel-actions">
            <div
              class="secure-flow__progress"
              role="tablist"
              aria-label="Secure flow steps"
            >
              <button
                *ngFor="let step of steps; let i = index"
                type="button"
                class="secure-flow__progress-tick"
                [class.secure-flow__progress-tick--done]="isStepComplete(i)"
                [class.secure-flow__progress-tick--current]="isStepCurrent(i)"
                [attr.aria-label]="'Go to step ' + step.id + ': ' + step.title"
                [attr.aria-current]="isStepCurrent(i) ? 'step' : null"
                (click)="goToStep(i)"
              ></button>
            </div>

            <button type="button" class="secure-flow__next" (click)="nextStep()">
              {{ isLastStep ? 'Start over' : 'Next step' }}
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M8.79 2.29a1 1 0 0 1 1.42 0l4.5 4.5a1 1 0 0 1 0 1.42l-4.5 4.5a1 1 0 0 1-1.42-1.42L11.09 9H2a1 1 0 0 1 0-2h9.09L8.79 3.71a1 1 0 0 1 0-1.42Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </aside>
      </div>
    </div>
    <p class="secure-flow__footnote">
      Neither your model nor the benchmarking datasets are exposed during this process.
    </p>
  </div>
</section>
